import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const WhatsAppSettings = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', body: '', category: 'utility' });

  // Fetch WhatsApp settings
  const { data: settings, refetch: refetchSettings } = useQuery({
    queryKey: ['whatsapp-settings'],
    queryFn: async () => {
      try {
        const records = await pb.collection('whatsapp_settings').getFullList({ filter: 'is_active=true' });
        return records[0] || null;
      } catch {
        return null;
      }
    },
    enabled: !!currentUser
  });

  // Fetch templates
  const { data: templates = [], refetch: refetchTemplates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => pb.collection('whatsapp_templates').getFullList({ sort: 'name' }),
    enabled: !!currentUser
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => {
      if (settings?.id) {
        return await pb.collection('whatsapp_settings').update(settings.id, data);
      } else {
        return await pb.collection('whatsapp_settings').create(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp-settings']);
      refetchSettings();
      toast.success('Settings saved successfully');
    },
    onError: (error) => {
      toast.error('Failed to save settings: ' + (error.message || 'Unknown error'));
    }
  });

  // Template mutations
  const createTemplateMutation = useMutation({
    mutationFn: (data) => pb.collection('whatsapp_templates').create(data),
    onSuccess: () => {
      refetchTemplates();
      setTemplateDialogOpen(false);
      toast.success('Template created');
      setTemplateForm({ name: '', body: '', category: 'utility' });
      setEditingTemplate(null);
    },
    onError: (error) => toast.error('Failed to create template: ' + (error.message || 'Unknown error'))
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }) => pb.collection('whatsapp_templates').update(id, data),
    onSuccess: () => {
      refetchTemplates();
      setTemplateDialogOpen(false);
      toast.success('Template updated');
      setTemplateForm({ name: '', body: '', category: 'utility' });
      setEditingTemplate(null);
    },
    onError: (error) => toast.error('Failed to update template: ' + (error.message || 'Unknown error'))
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id) => pb.collection('whatsapp_templates').delete(id),
    onSuccess: () => {
      refetchTemplates();
      toast.success('Template deleted');
    },
    onError: (error) => toast.error('Failed to delete template: ' + (error.message || 'Unknown error'))
  });

  const handleSaveSettings = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateSettingsMutation.mutate({
      phone_number_id: formData.get('phone_number_id'),
      access_token: formData.get('access_token'),
      business_account_id: formData.get('business_account_id'),
      webhook_verify_token: formData.get('webhook_verify_token'),
      auto_reply_enabled: formData.get('auto_reply_enabled') === 'on',
      auto_reply_message: formData.get('auto_reply_message'),
      is_active: true
    });
  };

  // Check access
  if (!currentUser || !['admin', 'manager'].includes(currentUser.role)) {
    return (
      <div className="text-center py-10">
        <div className="text-red-500 text-lg font-semibold">Access Denied</div>
        <p className="text-slate-500">Only admins and managers can access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <h1 className="text-2xl font-bold text-slate-800">WhatsApp Business Settings</h1>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">API Configuration</h2>
        <form onSubmit={handleSaveSettings} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Phone Number ID *</label>
            <input
              name="phone_number_id"
              defaultValue={settings?.phone_number_id}
              placeholder="From Meta WhatsApp Business"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Access Token *</label>
            <input
              name="access_token"
              defaultValue={settings?.access_token}
              type="password"
              placeholder="Your permanent access token"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Business Account ID</label>
            <input
              name="business_account_id"
              defaultValue={settings?.business_account_id}
              placeholder="WhatsApp Business Account ID"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Webhook Verify Token</label>
            <input
              name="webhook_verify_token"
              defaultValue={settings?.webhook_verify_token}
              placeholder="Your webhook verification token"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div className="border-t pt-4">
            <h3 className="font-medium text-sm text-slate-700 mb-3">Auto Reply Settings</h3>
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                name="auto_reply_enabled"
                defaultChecked={settings?.auto_reply_enabled || false}
                className="w-4 h-4 text-indigo-600"
              />
              <label className="text-sm text-slate-700">Enable Auto Reply</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Auto Reply Message</label>
              <textarea
                name="auto_reply_message"
                defaultValue={settings?.auto_reply_message}
                placeholder="Thank you for your message. We'll get back to you soon."
                rows={3}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={updateSettingsMutation.isPending}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>

      {/* Templates Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Message Templates</h2>
          <button
            onClick={() => {
              setTemplateForm({ name: '', body: '', category: 'utility' });
              setEditingTemplate(null);
              setTemplateDialogOpen(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 transition"
          >
            + Add Template
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <p>No templates created yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Body</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} className="border-t">
                    <td className="p-3 font-medium">{t.name}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 bg-slate-100 rounded-full text-xs capitalize">{t.category}</span>
                    </td>
                    <td className="p-3 text-slate-600 max-w-md truncate">{t.body}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => {
                          setEditingTemplate(t);
                          setTemplateForm({ name: t.name, body: t.body, category: t.category });
                          setTemplateDialogOpen(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this template?')) {
                            deleteTemplateMutation.mutate(t.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Webhook Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Webhook Configuration</h2>
        <div className="bg-slate-50 p-4 rounded-lg">
          <p className="text-sm text-slate-500 mb-2">Your Webhook URL:</p>
          <code className="block bg-white p-3 rounded border text-sm font-mono break-all">
            {window.location.origin}/api/whatsapp/webhook
          </code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/api/whatsapp/webhook`);
              toast.success('Webhook URL copied!');
            }}
            className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm"
          >
            Copy URL
          </button>
        </div>
        <div className="mt-4 text-sm text-slate-500 bg-blue-50 p-4 rounded-lg">
          <p className="font-medium text-blue-700">Setup Instructions:</p>
          <ol className="list-decimal pl-4 space-y-1 mt-1">
            <li>Go to Meta Developer Portal</li>
            <li>Select your WhatsApp Business app</li>
            <li>Add the webhook URL above</li>
            <li>Set the Verify Token (same as in settings)</li>
            <li>Subscribe to messages and message_deliveries events</li>
          </ol>
        </div>
      </div>

      {/* Template Dialog */}
      {templateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">{editingTemplate ? 'Edit Template' : 'New Template'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Template Name *</label>
                <input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  placeholder="e.g., welcome_message"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Category</label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="utility">Utility</option>
                  <option value="marketing">Marketing</option>
                  <option value="authentication">Authentication</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Template Body *</label>
                <textarea
                  rows={4}
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                  placeholder="Use {{1}}, {{2}}, {{3}} for variables"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setTemplateDialogOpen(false)}
                className="px-4 py-2 border rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!templateForm.name || !templateForm.body) {
                    toast.error('Please fill all required fields');
                    return;
                  }
                  if (editingTemplate) {
                    updateTemplateMutation.mutate({ id: editingTemplate.id, data: templateForm });
                  } else {
                    createTemplateMutation.mutate(templateForm);
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                {editingTemplate ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSettings;
